import React from 'react';
import './ResumeWorkflowCard.css';

interface ResumeWorkflowCardProps {
  contextId: string;
  interruptedAt: string;
  reason?: string;
  currentPhase?: number;
  canResume: boolean;
  onResume: () => void;
  onRestart: () => void;
  onCancel: () => void;
}

const ResumeWorkflowCard: React.FC<ResumeWorkflowCardProps> = ({
  contextId,
  interruptedAt,
  reason,
  currentPhase = 1,
  canResume,
  onResume,
  onRestart,
  onCancel,
}) => {
  const getPhaseLabel = (phase: number) => {
    switch (phase) {
      case 1:
        return 'Requirements Analysis';
      case 2:
        return 'Script Generation';
      case 3:
        return 'QA Review';
      case 4:
        return 'Revision';
      default:
        return `Phase ${phase}`;
    }
  };

  const formatTime = (timestamp: string) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins} minute${diffMins > 1 ? 's' : ''} ago`;
    
    const diffHours = Math.floor(diffMins / 60);
    if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
    
    return date.toLocaleString();
  };

  return (
    <div className="resume-workflow-card">
      <div className="resume-header">
        <div className="resume-icon">⚠️</div>
        <div className="resume-title">
          <h3>Workflow Interrupted</h3>
          <p className="resume-timestamp">{formatTime(interruptedAt)}</p>
        </div>
      </div>

      <div className="resume-details">
        <div className="resume-detail-item">
          <span className="detail-label">Context ID:</span>
          <span className="detail-value context-id">{contextId.substring(0, 12)}...</span>
        </div>
        
        <div className="resume-detail-item">
          <span className="detail-label">Interrupted at:</span>
          <span className="detail-value">{getPhaseLabel(currentPhase)}</span>
        </div>
        
        {reason && (
          <div className="resume-detail-item">
            <span className="detail-label">Reason:</span>
            <span className="detail-value">{reason}</span>
          </div>
        )}
      </div>

      <div className="resume-progress">
        <div className="progress-label">Workflow Progress</div>
        <div className="progress-bar">
          <div 
            className="progress-fill" 
            style={{ width: `${(currentPhase / 4) * 100}%` }}
          />
        </div>
        <div className="progress-text">{currentPhase} of 4 phases completed</div>
      </div>

      <div className="resume-info">
        <p>
          {canResume 
            ? '✅ Your workflow context has been saved. You can resume from where you left off or start fresh.'
            : '⚠️ This workflow cannot be resumed due to context expiration. You can restart with the same parameters.'}
        </p>
      </div>

      <div className="resume-actions">
        {canResume && (
          <button className="btn-resume-primary" onClick={onResume}>
            <span className="btn-icon">▶️</span>
            Resume from Phase {currentPhase}
          </button>
        )}
        
        <button className="btn-resume-secondary" onClick={onRestart}>
          <span className="btn-icon">🔄</span>
          Restart Workflow
        </button>
        
        <button className="btn-resume-tertiary" onClick={onCancel}>
          <span className="btn-icon">❌</span>
          Cancel
        </button>
      </div>

      {canResume && (
        <div className="resume-help">
          <p>
            💡 <strong>Tip:</strong> Resuming will continue exactly where you left off, 
            preserving all your requirements and generated scripts.
          </p>
        </div>
      )}
    </div>
  );
};

export default ResumeWorkflowCard;